import type { Client } from '@/types';

export interface ClientOption {
  value: string;
  label: string;
  hint?: string;
}

const NO_BILLING_PARTY = 'No billing party';

function nameKey(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Names that appear more than once in the list, compared trimmed and
 * case-insensitively. Only those get the billing party appended.
 */
function duplicateNames(clients: readonly Client[]): Set<string> {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const client of clients) {
    const key = nameKey(client.name);
    if (seen.has(key)) dupes.add(key);
    else seen.add(key);
  }
  return dupes;
}

/**
 * Display label for one client. When another client in `all` shares the same
 * name, the billing party is appended so the two can be told apart.
 */
export function clientLabel(client: Client, all: readonly Client[]): string {
  const dupes = duplicateNames(all);
  return labelFor(client, dupes);
}

function labelFor(client: Client, dupes: Set<string>): string {
  if (!dupes.has(nameKey(client.name))) return client.name;
  return `${client.name} (${client.billable?.name ?? NO_BILLING_PARTY})`;
}

/**
 * Options for a client picker. `hint` carries the billing party for pickers
 * that render a subtitle; pickers that only read value/label can ignore it.
 */
export function clientOptions(clients: readonly Client[]): ClientOption[] {
  const dupes = duplicateNames(clients);
  return clients.map((client) => {
    const option: ClientOption = { value: client.id, label: labelFor(client, dupes) };
    if (client.billable?.name) option.hint = client.billable.name;
    return option;
  });
}
